// Custom hook for login authentication logic
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { apiUrl } from '../api';
import { encryptText } from '../utils/cryptoUtils';
import { message } from 'antd';

export const useLoginAuth = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [forgotemail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState(0);
  const [otpToken, setOtpToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [apiLoading, setApiLoading] = useState(false);

  const { login, setRole, setName, setemail } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setApiLoading(true);
    setError(null);

    try {
      const publicKeyResponse = await fetch(`${apiUrl}/auth/public-key`);
      if (!publicKeyResponse.ok) throw new Error('Failed to fetch public key');
      
      const { pemKey } = await publicKeyResponse.json();
      const encryptedUsername = await encryptText(pemKey, username);
      const encryptedPassword = await encryptText(pemKey, password);

      const response = await fetch(`${apiUrl}/auth/get-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: encryptedUsername, password: encryptedPassword }),
      });

      if (response.ok) {
        const { accessToken, role, fullName, email } = await response.json();
        setRole(role);
        setName(fullName || username);
        setemail(email);
        login(accessToken);
        
        if (role === 'ADMIN') {
          navigate('/neopass-admin');
        } else if (role === 'ECR_PROVIDER') {
          navigate('/ecr-provider');
        } else if (role === 'TERMINAL_MANAGER') {
          navigate('/terminals');
        } else if (role === 'MSP_USER') {
          navigate('/msp-portal');
        } else {
          navigate('/dashboard');
        }
      } else if (response.status === 511) {
        const { token } = await response.json();
        document.cookie = `tdkn=${token}; path=/; max-age=${60 * 60 * 24 * 30}; secure; SameSite=none`;
        setOtpToken(token);
        return 'otp-required';
      } else {
        const { message: errorMessage } = await response.json();
        setError(errorMessage);
      }
    } catch (error) {
      console.error(error);
      setError('An error occurred during login');
    } finally {
      setApiLoading(false);
    }
  };

  const handleVerify = async (t: (key: string) => string) => {
    if (otp.toString().length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    setError(null);

    try {
      const response = await fetch(`${apiUrl}/auth/validate-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: otpToken, otp }),
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 400) {
          setError(t('Incorrect OTP'));
        }
        throw new Error('OTP validation failed');
      }

      if (response.status === 200) {
        const { accessToken, role, fullName, email } = await response.json();
        setRole(role);
        setName(fullName || username);
        setemail(email);
        login(accessToken);
        
        if (role === 'ADMIN') {
          navigate('/neopass-admin');
        } else if (role === 'ECR_PROVIDER') {
          navigate('/ecr-provider');
        } else if (role === 'TERMINAL_MANAGER') {
          navigate('/terminals');
        } else if (role === 'MSP_USER') {
          navigate('/msp-portal');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleReset = async () => {
    const formData = new FormData();
    formData.append('email', forgotemail);

    setApiLoading(true);
    
    try {
      const response = await fetch(`${apiUrl}/user-password/forgot-password`, {
        method: 'POST',
        body: formData,
      });

      if (response.status === 200) {
        message.success('If the email is registered, a reset email is sent.');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setApiLoading(false);
    }
  };

  return {
    username,
    setUsername,
    password,
    setPassword,
    forgotemail,
    setForgotEmail,
    otp,
    setOtp,
    error,
    setError,
    apiLoading,
    handleSubmit,
    handleVerify,
    handleReset,
  };
};
